import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, useReactFlow, Panel,
  type Node, type Edge, type NodeMouseHandler, type EdgeMouseHandler,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Network, Plus, Filter, Eye, Focus, UnfoldVertical, FoldVertical,
  Maximize2, Minimize2, ShieldAlert,
} from "lucide-react";
import type { StrainGraph, GraphNode, GraphEdge, NodeType, RelationType, ID } from "@/types";
import { useStore } from "@/store/useStore";
import { StrainNode, type AttachedModifier, type StrainNodeData } from "./StrainNode";
import { StrainEdge, type EdgeCritiqueCard, type StrainEdgeData } from "./StrainEdge";
import { nodeMeta, edgeMeta, EDGE_TYPES, NODE_TYPES } from "@/lib/vocabulary";
import { downstreamNodes, upstreamNodes } from "@/lib/graph";
import { autoLayout } from "@/lib/layout";
import { cn } from "@/lib/cn";
import { AddEdgeModal } from "./AddEdgeModal";

const nodeTypes = { strain: StrainNode };
const edgeTypes = { strain: StrainEdge };

const CRITIQUE_RELATIONS = new Set<RelationType>(["rebuts", "undercuts", "credibility"]);
const DEFAULT_FIT_PADDING = 0.3;

type HiddenCritiqueSummary = {
  count: number;
  revealNodeId?: ID;
};

type CritiqueAnchor = {
  anchorEdgeId?: ID;
  rootTargetId?: ID;
};

type CritiqueFocus = {
  active: boolean;
  nodeIds: Set<ID>;
  edgeIds: Set<ID>;
};

function isCritiqueEdge(edge: GraphEdge): boolean {
  return CRITIQUE_RELATIONS.has(edge.relation);
}

function anchorRelationRank(edge: GraphEdge): number {
  switch (edge.relation) {
    case "defines": return 0;
    case "evidences": return 1;
    case "supports": return 2;
    case "qualifies": return 3;
    case "reframes": return 4;
    default: return 8;
  }
}

function nodeVisualRole(node: GraphNode, critiqueSourceIds: Set<ID>): StrainNodeData["visualRole"] {
  if (node.badges?.includes("thesis")) return "thesis";
  if (critiqueSourceIds.has(node.id)) return "critique";
  if (node.type === "bridge") return "bridge";
  if (node.type === "evidence" || node.type === "example" || node.type === "measurement") return "evidence";
  if (node.type === "hidden_warrant" || node.type === "scope_condition") return "warrant";
  if (node.type === "criterion" || node.type === "definition" || node.type === "terminal_value") return "criterion";
  return "default";
}

function critiqueTouchesSelection(
  edge: GraphEdge,
  selectedNodeId: ID | null,
  selectedEdgeId: ID | null,
  edgeById: Map<ID, GraphEdge>,
): boolean {
  if (!isCritiqueEdge(edge)) return false;
  if (selectedEdgeId && (edge.id === selectedEdgeId || edge.target_edge === selectedEdgeId)) return true;
  if (!selectedNodeId) return false;
  if (edge.source === selectedNodeId || edge.target === selectedNodeId) return true;
  if (!edge.target_edge) return false;
  const targetEdge = edgeById.get(edge.target_edge);
  return !!targetEdge && (targetEdge.source === selectedNodeId || targetEdge.target === selectedNodeId);
}

function markerEndForEdge(edge: GraphEdge) {
  const meta = edgeMeta(edge.relation);
  return {
    type: MarkerType.ArrowClosed,
    width: meta.arrow === "thin" ? 18 : 23,
    height: meta.arrow === "thin" ? 18 : 23,
    color: meta.color,
  };
}

// build the data payload for each flow node (position-independent)
function buildNodeData(
  graph: StrainGraph,
  n: GraphNode,
  selectedNodeId: ID | null,
  selectedEdgeId: ID | null,
  highlightSet: Set<ID>,
  highlightActive: boolean,
  focus: CritiqueFocus,
  critiqueSourceIds: Set<ID>,
  hiddenCritiqueCounts: Map<ID, number>,
  onRevealCritiques: (nodeId?: ID, edgeId?: ID) => void,
  attachedModifiers: AttachedModifier[],
  selectedModifierId: ID | null,
  onSelectModifier: (nodeId: ID) => void,
  onPreferVariant: (nodeId: ID, variantId: ID) => void,
  preferredVariantIds: ID[],
): StrainNodeData {
  const isSelected = selectedNodeId === n.id;
  const isHighlighted = highlightSet.has(n.id);
  const isDimmed = (
    (highlightActive && selectedNodeId ? !isSelected && !isHighlighted : false)
    || (focus.active ? !focus.nodeIds.has(n.id) : false)
  );
  return {
    node: n,
    isSelected,
    isHighlighted,
    isDimmed,
    visualRole: nodeVisualRole(n, critiqueSourceIds),
    hiddenCritiqueCount: hiddenCritiqueCounts.get(n.id) ?? 0,
    onRevealCritiques,
    attachedModifiers,
    selectedModifierId,
    onSelectModifier,
    onPreferVariant,
    preferredVariantIds,
  };
}

function buildEdgeData(
  e: GraphEdge,
  selectedNodeId: ID | null,
  selectedEdgeId: ID | null,
  focus: CritiqueFocus,
  hiddenCritiqueSummary: HiddenCritiqueSummary | undefined,
  critiqueCards: EdgeCritiqueCard[],
  onRevealCritiques: (nodeId?: ID, edgeId?: ID) => void,
  onSelectNode: (nodeId: ID) => void,
): StrainEdgeData {
  const isSelected = selectedEdgeId === e.id;
  const isHighlighted = !!(selectedNodeId && (e.source === selectedNodeId || e.target === selectedNodeId));
  const baseDimmed = !!(selectedNodeId && !isSelected && !isHighlighted && !e.target_edge);
  const isDimmed = focus.active ? !focus.edgeIds.has(e.id) : baseDimmed;
  return {
    edge: e,
    isSelected,
    isHighlighted,
    isDimmed,
    hiddenCritiqueCount: hiddenCritiqueSummary?.count ?? 0,
    revealNodeId: hiddenCritiqueSummary?.revealNodeId,
    onRevealCritiques,
    critiqueCards,
    onSelectNode,
  };
}

function InnerCanvas({
  graph, fullscreen, onToggleFullscreen,
}: { graph: StrainGraph; fullscreen: boolean; onToggleFullscreen: () => void }) {
  // field-level subscriptions: the canvas re-renders on selection/theme
  // changes only, not on every unrelated store mutation. zustand actions are
  // stable references, so they are safe in dependency arrays.
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const selectNode = useStore((s) => s.selectNode);
  const selectEdge = useStore((s) => s.selectEdge);
  const theme = useStore((s) => s.theme);
  const updateNodePosition = useStore((s) => s.updateNodePosition);
  const clearPositionOverrides = useStore((s) => s.clearPositionOverrides);
  const addNode = useStore((s) => s.addNode);
  const preferVariant = useStore((s) => s.preferVariant);
  const reactions = useStore((s) => s.reactions);
  const rf = useReactFlow();

  const preferredVariantIds = useMemo(() => (
    Object.keys(reactions).filter((id) => reactions[id]?.includes("prefer_wording"))
  ), [reactions]);

  // local view state
  const [highlightUp, setHighlightUp] = useState(false);
  const [highlightDown, setHighlightDown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [nodeTypeFilter, setNodeTypeFilter] = useState<Set<NodeType>>(new Set());
  const [edgeTypeFilter, setEdgeTypeFilter] = useState<Set<RelationType>>(new Set());
  const [showInferred, setShowInferred] = useState(true);
  const [showAllCritiques, setShowAllCritiques] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<ID>>(new Set());
  const [addEdgeOpen, setAddEdgeOpen] = useState(false);
  const [addNodeAt, setAddNodeAt] = useState<{ x: number; y: number } | null>(null);
  const [addNodeText, setAddNodeText] = useState("");

  // ---- layout: compute once per graph, merge with any store-persisted positions ----
  // the auto-layout gives clean defaults; store positions (from drag-stop) override.
  // this runs only when the strain changes, so clicking a node never re-layouts.
  // (layoutGraph is defined below once the modifier maps exist.)

  const nodeById = useMemo(() => new Map(graph.nodes.map((node) => [node.id, node])), [graph.nodes]);
  const edgeById = useMemo(() => new Map(graph.edges.map((edge) => [edge.id, edge])), [graph.edges]);

  // ---- modifiers: attachment nodes dock onto their anchor node ----
  // an attachment node with a qualifies-style link to a target renders as a
  // docked card on that target instead of a peer node. orphans (no link)
  // stay in the main graph so nothing silently disappears.
  const modifiersByParent = useMemo(() => {
    const map = new Map<ID, AttachedModifier[]>();
    graph.nodes.forEach((node) => {
      if (!node.attachment) return;
      const link = graph.edges.find((e) => e.source === node.id && e.target && !isCritiqueEdge(e));
      if (!link?.target) return;
      const list = map.get(link.target) ?? [];
      list.push({ node, edge: link });
      map.set(link.target, list);
    });
    map.forEach((list) => list.sort((a, b) => {
      const ka = a.node.attachment?.kind === "exception" ? 0 : 1;
      const kb = b.node.attachment?.kind === "exception" ? 0 : 1;
      if (ka !== kb) return ka - kb;
      return a.node.id.localeCompare(b.node.id);
    }));
    return map;
  }, [graph.edges, graph.nodes]);
  const modifierParentById = useMemo(() => {
    const map = new Map<ID, ID>();
    modifiersByParent.forEach((list, parentId) => list.forEach((m) => map.set(m.node.id, parentId)));
    return map;
  }, [modifiersByParent]);
  const modifierEdgeIds = useMemo(() => {
    const ids = new Set<ID>();
    modifiersByParent.forEach((list) => list.forEach((m) => ids.add(m.edge.id)));
    return ids;
  }, [modifiersByParent]);

  // selecting a docked modifier keeps its parent expanded in the canvas
  const selectedModifierId = selectedNodeId && modifierParentById.has(selectedNodeId) ? selectedNodeId : null;
  const effectiveSelectedNodeId = selectedModifierId
    ? modifierParentById.get(selectedModifierId) ?? selectedNodeId
    : selectedNodeId;

  // docked modifiers and their link edges do not participate in layout
  const layoutGraph = useMemo<StrainGraph>(() => ({
    ...graph,
    nodes: graph.nodes.filter((n) => !modifierParentById.has(n.id)),
    edges: graph.edges.filter((e) => (
      !modifierEdgeIds.has(e.id)
      && !modifierParentById.has(e.source)
      && (!e.target || !modifierParentById.has(e.target))
    )),
  }), [graph, modifierEdgeIds, modifierParentById]);
  const layoutPositions = useMemo(() => autoLayout(layoutGraph), [layoutGraph]);
  const nonCritiqueEdges = useMemo(() => (
    graph.edges.filter((edge) => !isCritiqueEdge(edge) && edge.target)
  ), [graph.edges]);
  const terminalNodeIds = useMemo(() => {
    const hasOutgoingMainEdge = new Set<ID>(nonCritiqueEdges.map((edge) => edge.source));
    return new Set<ID>(
      graph.nodes
        .filter((node) => node.badges?.includes("thesis") || !hasOutgoingMainEdge.has(node.id))
        .map((node) => node.id),
    );
  }, [graph.nodes, nonCritiqueEdges]);
  const parentEdgeByTargetNodeId = useMemo(() => {
    const byNode = new Map<ID, GraphEdge>();
    const sorted = [...nonCritiqueEdges].sort((a, b) => {
      const rank = anchorRelationRank(a) - anchorRelationRank(b);
      if (rank !== 0) return rank;
      return a.id.localeCompare(b.id);
    });
    sorted.forEach((edge) => {
      if (edge.target && !byNode.has(edge.target)) byNode.set(edge.target, edge);
    });
    return byNode;
  }, [nonCritiqueEdges]);
  const critiqueEdges = useMemo(() => graph.edges.filter(isCritiqueEdge), [graph.edges]);
  const critiqueAnchorByEdgeId = useMemo(() => {
    const anchors = new Map<ID, CritiqueAnchor>();
    critiqueEdges.forEach((edge) => {
      if (edge.target_edge) {
        anchors.set(edge.id, { anchorEdgeId: edge.target_edge });
        return;
      }
      if (!edge.target) return;
      const parentEdge = parentEdgeByTargetNodeId.get(edge.target);
      if (parentEdge && !terminalNodeIds.has(edge.target)) {
        anchors.set(edge.id, { anchorEdgeId: parentEdge.id });
      } else {
        anchors.set(edge.id, { rootTargetId: edge.target });
      }
    });
    return anchors;
  }, [critiqueEdges, parentEdgeByTargetNodeId, terminalNodeIds]);
  const critiqueSourceIds = useMemo(() => (
    new Set<ID>(critiqueEdges.map((edge) => edge.source))
  ), [critiqueEdges]);
  const edgeAttachedCritiqueEdgeIds = useMemo(() => {
    const ids = new Set<ID>();
    critiqueAnchorByEdgeId.forEach((anchor, edgeId) => {
      if (anchor.anchorEdgeId) ids.add(edgeId);
    });
    return ids;
  }, [critiqueAnchorByEdgeId]);
  const edgeAttachedCritiqueSourceIds = useMemo(() => (
    new Set<ID>(
      critiqueEdges
        .filter((edge) => edgeAttachedCritiqueEdgeIds.has(edge.id))
        .map((edge) => edge.source),
    )
  ), [critiqueEdges, edgeAttachedCritiqueEdgeIds]);
  const selectedCritiqueSourceIds = useMemo(() => {
    if (showAllCritiques) return new Set<ID>(critiqueSourceIds);
    const selected = new Set<ID>();
    critiqueEdges.forEach((edge) => {
      if (critiqueTouchesSelection(edge, effectiveSelectedNodeId, selectedEdgeId, edgeById)) {
        selected.add(edge.source);
        if (edge.target_edge) {
          const targetEdge = edgeById.get(edge.target_edge);
          if (targetEdge && isCritiqueEdge(targetEdge)) selected.add(targetEdge.source);
        }
      }
    });
    return selected;
  }, [critiqueEdges, critiqueSourceIds, edgeById, showAllCritiques, selectedEdgeId, effectiveSelectedNodeId]);

  const revealCritiques = useCallback((nodeId?: ID, edgeId?: ID) => {
    setShowAllCritiques(false);
    if (nodeId) {
      selectNode(nodeId);
      return;
    }
    if (edgeId) selectEdge(edgeId);
  }, [selectNode, selectEdge]);

  const selectNodeFromCard = useCallback((nodeId: ID) => {
    selectNode(nodeId);
  }, [selectNode]);

  const preferVariantForNode = useCallback((nodeId: ID, variantId: ID) => {
    void preferVariant(graph.strain.slug, nodeId, variantId);
  }, [graph.strain.slug, preferVariant]);

  // ---- the canonical node list (filtering only) ----
  const visibleNodes = useMemo(() => {
    const hiddenByCollapse = new Set<ID>();
    if (collapsed.size) {
      for (const cid of collapsed) {
        downstreamNodes(cid, graph.nodes, graph.edges).forEach((n) => hiddenByCollapse.add(n.id));
      }
    }
    return graph.nodes.filter((n) => {
      if (hiddenByCollapse.has(n.id)) return false;
      if (modifierParentById.has(n.id)) return false; // docked on its anchor node
      if (edgeAttachedCritiqueSourceIds.has(n.id)) return false;
      if (
        critiqueSourceIds.has(n.id)
        && !showAllCritiques
        && !selectedCritiqueSourceIds.has(n.id)
        && n.id !== selectedNodeId
      ) return false;
      if (nodeTypeFilter.size > 0 && !nodeTypeFilter.has(n.type)) return false;
      if (!showInferred && n.provenance === "ai_inferred" && !n.badges?.includes("thesis")) return false;
      return true;
    });
  }, [
    graph,
    collapsed,
    edgeAttachedCritiqueSourceIds,
    critiqueSourceIds,
    modifierParentById,
    nodeTypeFilter,
    selectedCritiqueSourceIds,
    showAllCritiques,
    showInferred,
    selectedNodeId,
  ]);

  const visibleEdges = useMemo(() => {
    const ids = new Set(visibleNodes.map((n) => n.id));
    return graph.edges.filter((e) => {
      if (modifierEdgeIds.has(e.id)) return false;
      if (edgeAttachedCritiqueEdgeIds.has(e.id)) return false;
      if (edgeTypeFilter.size > 0 && !edgeTypeFilter.has(e.relation)) return false;
      if (
        isCritiqueEdge(e)
        && !showAllCritiques
        && !selectedCritiqueSourceIds.has(e.source)
        && e.id !== selectedEdgeId
      ) return false;
      if (!ids.has(e.source)) return false;
      if (e.target && !ids.has(e.target)) return false;
      if (e.target_edge) {
        const targetEdge = edgeById.get(e.target_edge);
        if (!targetEdge) return false;
        if (!ids.has(targetEdge.source)) return false;
        if (targetEdge.target && !ids.has(targetEdge.target)) return false;
      }
      return true;
    });
  }, [
    graph.edges,
    visibleNodes,
    edgeAttachedCritiqueEdgeIds,
    edgeTypeFilter,
    edgeById,
    modifierEdgeIds,
    selectedCritiqueSourceIds,
    showAllCritiques,
    selectedEdgeId,
  ]);

  const hiddenCritiqueSummaries = useMemo(() => {
    const visibleEdgeIds = new Set(visibleEdges.map((edge) => edge.id));
    const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
    const visibleNonCritiqueEdges = visibleEdges.filter((edge) => !isCritiqueEdge(edge));
    const edgeCounts = new Map<ID, HiddenCritiqueSummary>();
    const nodeCounts = new Map<ID, number>();

    const addEdgeCount = (edgeId: ID, count: number, revealNodeId?: ID) => {
      const prev = edgeCounts.get(edgeId);
      edgeCounts.set(edgeId, {
        count: (prev?.count ?? 0) + count,
        revealNodeId: prev?.revealNodeId ?? revealNodeId,
      });
    };

    const addNodeCount = (nodeId: ID, count: number) => {
      nodeCounts.set(nodeId, (nodeCounts.get(nodeId) ?? 0) + count);
    };

    for (const edge of critiqueEdges) {
      if (edgeTypeFilter.size > 0 && !edgeTypeFilter.has(edge.relation)) continue;

      const anchor = critiqueAnchorByEdgeId.get(edge.id);
      const critiqueIsVisible = showAllCritiques
        || critiqueTouchesSelection(edge, effectiveSelectedNodeId, selectedEdgeId, edgeById);

      if (anchor?.anchorEdgeId) {
        if (critiqueIsVisible) continue;
        const anchorEdge = edgeById.get(anchor.anchorEdgeId);
        if (anchorEdge && visibleEdgeIds.has(anchorEdge.id)) {
          addEdgeCount(anchorEdge.id, 1, edge.target ?? anchorEdge.target ?? anchorEdge.source);
        }
        continue;
      }

      if (visibleEdgeIds.has(edge.id) || critiqueIsVisible) continue;
      if (anchor?.rootTargetId && visibleNodeIds.has(anchor.rootTargetId)) {
        addNodeCount(anchor.rootTargetId, 1);
      } else if (edge.target && visibleNodeIds.has(edge.target)) {
        addNodeCount(edge.target, 1);
      }
    }

    if (!showAllCritiques) {
      for (const edge of visibleNonCritiqueEdges) {
        const knownHidden = edgeCounts.get(edge.id)?.count ?? 0;
        const reportedUndercuts = edge.stats?.undercuts ?? 0;
        if (reportedUndercuts > knownHidden) {
          addEdgeCount(edge.id, reportedUndercuts - knownHidden, edge.target ?? edge.source);
        }
      }
    }

    return { edgeCounts, nodeCounts };
  }, [
    critiqueEdges,
    critiqueAnchorByEdgeId,
    edgeById,
    edgeTypeFilter,
    showAllCritiques,
    selectedEdgeId,
    effectiveSelectedNodeId,
    visibleEdges,
    visibleNodes,
  ]);

  const edgeCritiqueCards = useMemo(() => {
    const cards = new Map<ID, EdgeCritiqueCard[]>();

    for (const edge of critiqueEdges) {
      const anchor = critiqueAnchorByEdgeId.get(edge.id);
      if (!anchor?.anchorEdgeId) continue;
      if (edgeTypeFilter.size > 0 && !edgeTypeFilter.has(edge.relation)) continue;

      const node = nodeById.get(edge.source);
      if (!node) continue;
      if (nodeTypeFilter.size > 0 && !nodeTypeFilter.has(node.type)) continue;
      if (!showInferred && node.provenance === "ai_inferred" && !node.badges?.includes("thesis")) continue;

      const shouldShow = showAllCritiques
        || critiqueTouchesSelection(edge, effectiveSelectedNodeId, selectedEdgeId, edgeById);
      if (!shouldShow) continue;

      const list = cards.get(anchor.anchorEdgeId) ?? [];
      list.push({
        node,
        edge,
        isSelected: selectedNodeId === node.id || selectedEdgeId === edge.id,
      });
      cards.set(anchor.anchorEdgeId, list);
    }

    cards.forEach((list) => {
      list.sort((a, b) => {
        if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
        const rank = anchorRelationRank(a.edge) - anchorRelationRank(b.edge);
        if (rank !== 0) return rank;
        return a.edge.id.localeCompare(b.edge.id);
      });
    });

    return cards;
  }, [
    critiqueAnchorByEdgeId,
    critiqueEdges,
    edgeById,
    edgeTypeFilter,
    nodeById,
    nodeTypeFilter,
    showAllCritiques,
    showInferred,
    selectedEdgeId,
    selectedNodeId,
    effectiveSelectedNodeId,
  ]);

  const critiqueFocus = useMemo<CritiqueFocus>(() => {
    const nodeIds = new Set<ID>();
    const edgeIds = new Set<ID>();
    const selectedEdge = selectedEdgeId ? edgeById.get(selectedEdgeId) : undefined;
    const selectedNodeIsCritique = !!effectiveSelectedNodeId && critiqueSourceIds.has(effectiveSelectedNodeId);
    const selectedEdgeIsCritique = !!selectedEdge && isCritiqueEdge(selectedEdge);
    const localCritiquesVisible = !showAllCritiques && selectedCritiqueSourceIds.size > 0;

    if (!selectedNodeIsCritique && !selectedEdgeIsCritique && !localCritiquesVisible) {
      return { active: false, nodeIds, edgeIds };
    }

    const addEdgeEndpoints = (edge: GraphEdge) => {
      edgeIds.add(edge.id);
      nodeIds.add(edge.source);
      if (edge.target) nodeIds.add(edge.target);
    };

    const addCritiqueContext = (edge: GraphEdge) => {
      nodeIds.add(edge.source);
      if (edge.target) nodeIds.add(edge.target);

      const anchor = critiqueAnchorByEdgeId.get(edge.id);
      if (anchor?.anchorEdgeId) {
        const anchorEdge = edgeById.get(anchor.anchorEdgeId);
        if (anchorEdge) addEdgeEndpoints(anchorEdge);
      } else {
        edgeIds.add(edge.id);
      }

      if (edge.target_edge) {
        const targetEdge = edgeById.get(edge.target_edge);
        if (targetEdge) addEdgeEndpoints(targetEdge);
      }
      if (anchor?.rootTargetId) nodeIds.add(anchor.rootTargetId);
    };

    for (const edge of critiqueEdges) {
      const touchesSelection = critiqueTouchesSelection(
        edge,
        effectiveSelectedNodeId,
        selectedEdgeId,
        edgeById,
      );
      if (
        touchesSelection
        || (effectiveSelectedNodeId && edge.source === effectiveSelectedNodeId)
        || (selectedEdgeId && edge.id === selectedEdgeId)
        || (!showAllCritiques && selectedCritiqueSourceIds.has(edge.source))
      ) {
        addCritiqueContext(edge);
      }
    }

    return { active: nodeIds.size > 0 || edgeIds.size > 0, nodeIds, edgeIds };
  }, [
    critiqueAnchorByEdgeId,
    critiqueEdges,
    critiqueSourceIds,
    edgeById,
    selectedCritiqueSourceIds,
    showAllCritiques,
    selectedEdgeId,
    effectiveSelectedNodeId,
  ]);

  // ---- build the initial flow nodes ONCE per graph (positions from layout + store) ----
  const initialFlowNodes = useMemo<Node[]>(() => {
    const getOverride = useStore.getState().getPositionOverride;
    return visibleNodes.map((n) => {
      const override = getOverride(graph.strain.slug, n.id);
      const pos = override ?? layoutPositions[n.id] ?? n.position;
      return {
        id: n.id,
        type: "strain",
        position: pos,
        data: buildNodeData(
          graph,
          n,
          selectedNodeId,
          selectedEdgeId,
          new Set(),
          false,
          critiqueFocus,
          critiqueSourceIds,
          hiddenCritiqueSummaries.nodeCounts,
          revealCritiques,
          modifiersByParent.get(n.id) ?? [],
          selectedModifierId,
          selectNodeFromCard,
          preferVariantForNode,
          preferredVariantIds,
        ),
        draggable: true,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.strain.slug]);

  const initialFlowEdges = useMemo<Edge[]>(() => {
    return visibleEdges.map((e) => {
      return {
        id: e.id,
        type: "strain",
        source: e.source,
        target: e.target ?? e.source,
        data: buildEdgeData(
          e,
          effectiveSelectedNodeId,
          selectedEdgeId,
          critiqueFocus,
          hiddenCritiqueSummaries.edgeCounts.get(e.id),
          edgeCritiqueCards.get(e.id) ?? [],
          revealCritiques,
          selectNodeFromCard,
        ),
        markerEnd: markerEndForEdge(e),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.strain.slug]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialFlowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialFlowEdges);

  // refit view when the graph itself changes (not on selection)
  const fitView = useReactFlow().fitView;
  useEffect(() => {
    setNodes(initialFlowNodes);
    setEdges(initialFlowEdges);
    // refit after the new nodes render
    requestAnimationFrame(() => {
      try { fitView({ padding: DEFAULT_FIT_PADDING, maxZoom: 1.05 }); } catch {}
    });
  }, [initialFlowNodes, initialFlowEdges, setNodes, setEdges, fitView]);

  // ---- update node DATA (selection/highlight) WITHOUT touching positions ----
  // this is the fix for "clicking a node resets dragged positions".
  // we patch only the data field, leaving position intact.
  useEffect(() => {
    const highlightSet = new Set<ID>();
    if (highlightUp && effectiveSelectedNodeId) {
      upstreamNodes(effectiveSelectedNodeId, graph.nodes, graph.edges).forEach((n) => highlightSet.add(n.id));
    }
    if (highlightDown && effectiveSelectedNodeId) {
      downstreamNodes(effectiveSelectedNodeId, graph.nodes, graph.edges).forEach((n) => highlightSet.add(n.id));
    }
    const highlightActive = (highlightUp || highlightDown) && !!effectiveSelectedNodeId;
    const getOverride = useStore.getState().getPositionOverride;

    setNodes((cur) => {
      const current = new Map(cur.map((node) => [node.id, node]));
      return visibleNodes.map((gnode) => {
        const existing = current.get(gnode.id);
        const override = getOverride(graph.strain.slug, gnode.id);
        const position = existing?.position ?? override ?? layoutPositions[gnode.id] ?? gnode.position;
        return {
          ...(existing ?? {}),
          id: gnode.id,
          type: "strain",
          position,
          data: buildNodeData(
            graph,
            gnode,
            effectiveSelectedNodeId,
            selectedEdgeId,
            highlightSet,
            highlightActive,
            critiqueFocus,
            critiqueSourceIds,
            hiddenCritiqueSummaries.nodeCounts,
            revealCritiques,
            modifiersByParent.get(gnode.id) ?? [],
            selectedModifierId,
            selectNodeFromCard,
            preferVariantForNode,
            preferredVariantIds,
          ),
          draggable: true,
        };
      });
    });
    setEdges((cur) => {
      const current = new Map(cur.map((edge) => [edge.id, edge]));
      return visibleEdges.map((gedge) => {
        const existing = current.get(gedge.id);
        return {
          ...(existing ?? {}),
          id: gedge.id,
          type: "strain",
          source: gedge.source,
          target: gedge.target ?? gedge.source,
          data: buildEdgeData(
            gedge,
            effectiveSelectedNodeId,
            selectedEdgeId,
            critiqueFocus,
            hiddenCritiqueSummaries.edgeCounts.get(gedge.id),
            edgeCritiqueCards.get(gedge.id) ?? [],
            revealCritiques,
            selectNodeFromCard,
          ),
          markerEnd: markerEndForEdge(gedge),
        };
      });
    });
  }, [
    critiqueFocus,
    critiqueSourceIds,
    edgeCritiqueCards,
    graph,
    hiddenCritiqueSummaries,
    highlightDown,
    highlightUp,
    layoutPositions,
    modifiersByParent,
    preferVariantForNode,
    preferredVariantIds,
    revealCritiques,
    selectNodeFromCard,
    selectedEdgeId,
    selectedModifierId,
    effectiveSelectedNodeId,
    setEdges,
    setNodes,
    visibleEdges,
    visibleNodes,
  ]);

  useEffect(() => {
    if (!showAllCritiques) return;
    requestAnimationFrame(() => {
      try { rf.fitView({ padding: DEFAULT_FIT_PADDING, maxZoom: 1.05, duration: 180 }); } catch {}
    });
  }, [rf, showAllCritiques, visibleNodes.length]);

  useEffect(() => {
    if (showAllCritiques || !critiqueFocus.active) return;
    const focusNodes = visibleNodes
      .filter((node) => critiqueFocus.nodeIds.has(node.id))
      .map((node) => ({ id: node.id }));
    if (focusNodes.length === 0) return;
    requestAnimationFrame(() => {
      try { rf.fitView({ nodes: focusNodes, padding: 0.55, maxZoom: 1.05, duration: 180 }); } catch {}
    });
  }, [critiqueFocus, rf, showAllCritiques, visibleNodes]);

  // ---- handlers ----
  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    selectNode(node.id);
  }, [selectNode]);

  const onEdgeClick: EdgeMouseHandler = useCallback((_, edge) => {
    selectEdge(edge.id);
  }, [selectEdge]);

  const onPaneClick = useCallback(() => {
    selectNode(null);
    selectEdge(null);
  }, [selectNode, selectEdge]);

  const onNodeDragStop = useCallback((_evt: MouseEvent | TouchEvent, node: Node) => {
    updateNodePosition(graph.strain.slug, node.id, node.position.x, node.position.y);
  }, [graph.strain.slug, updateNodePosition]);

  const onDoubleClick = useCallback((evt: React.MouseEvent) => {
    const bounds = (evt.currentTarget as HTMLElement).getBoundingClientRect();
    setAddNodeAt({
      x: evt.clientX - bounds.left - 110,
      y: evt.clientY - bounds.top - 30,
    });
  }, []);

  const commitAddNode = () => {
    if (!addNodeText.trim() || !addNodeAt) return;
    addNode(graph.strain.slug, {
      type: "claim",
      epistemic_kind: "evaluative",
      text: addNodeText.trim(),
      provenance: "user",
      status: "active",
      position: addNodeAt,
      badges: [],
      // no invented stance data on a brand-new node
      stats: { comments: 0, evidence: 0, forks: 0, contention: 0, mind_changes: 0 },
    });
    setAddNodeText("");
    setAddNodeAt(null);
  };

  const toggleFilter = <T,>(set: Set<T>, v: T, setter: (s: Set<T>) => void) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    setter(next);
  };

  const selectedNode = graph.nodes.find((n) => n.id === effectiveSelectedNodeId);
  const canCollapse = selectedNode && downstreamNodes(selectedNode.id, graph.nodes, graph.edges).length > 0;

  return (
    <div className={cn(
      "relative w-full h-full bg-grid",
      fullscreen && "fixed inset-0 z-50 bg-[var(--color-base)]",
    )} onDoubleClick={onDoubleClick}>
      <ReactFlow
        className="idea-flow"
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        colorMode={theme === "dark" ? "dark" : "light"}
        fitView
        fitViewOptions={{ padding: DEFAULT_FIT_PADDING, maxZoom: 1.05 }}
        minZoom={0.15}
        maxZoom={2.4}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodesConnectable={false}
        defaultEdgeOptions={{ type: "strain" }}
      >
        <Background gap={32} size={1} color="rgba(255,255,255,0.025)" />
        <Controls
          className="!bg-[var(--color-surface)] !border-[var(--color-border)] !rounded-lg !shadow-card"
          showInteractive={false}
        />
        <MiniMap
          position="bottom-left"
          className="!left-14 !bg-[var(--color-surface)] !border-[var(--color-border)] !rounded-lg"
          maskColor={theme === "dark" ? "rgba(6,8,15,0.7)" : "rgba(231,234,242,0.75)"}
          nodeColor={(n) => {
            const d = n.data as StrainNodeData;
            return nodeMeta(d.node.type).color;
          }}
          pannable
          zoomable
        />

        {/* top-left toolbar */}
        <Panel position="top-left">
          <div className="flex items-center gap-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/90 backdrop-blur p-1 shadow-card">
            <ToolButton active={highlightUp} onClick={() => setHighlightUp((v) => !v)} icon={<UnfoldVertical size={15} />} label="Upstream" />
            <ToolButton active={highlightDown} onClick={() => setHighlightDown((v) => !v)} icon={<FoldVertical size={15} />} label="Downstream" />
            <div className="w-px h-5 bg-[var(--color-border)] mx-0.5" />
            <ToolButton onClick={() => setShowFilters((v) => !v)} active={showFilters || nodeTypeFilter.size > 0 || edgeTypeFilter.size > 0} icon={<Filter size={15} />} label="Filter" />
            <ToolButton onClick={() => setShowInferred((v) => !v)} active={!showInferred} icon={<Eye size={15} />} label={showInferred ? "Hide inferred" : "Show inferred"} />
            {critiqueEdges.length > 0 && (
              <ToolButton
                onClick={() => setShowAllCritiques((v) => !v)}
                active={showAllCritiques}
                icon={<ShieldAlert size={15} />}
                label={showAllCritiques ? "Clean read" : "Critiques"}
              />
            )}
            {canCollapse && (
              <ToolButton
                onClick={() => {
                  const next = new Set(collapsed);
                  if (next.has(selectedNode!.id)) next.delete(selectedNode!.id);
                  else next.add(selectedNode!.id);
                  setCollapsed(next);
                }}
                active={collapsed.has(selectedNode?.id ?? "")}
                icon={<Network size={15} />}
                label={collapsed.has(selectedNode?.id ?? "") ? "Expand" : "Collapse"}
              />
            )}
          </div>
        </Panel>

        {/* top-right quick actions */}
        <Panel position="top-right">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                const positions = autoLayout(layoutGraph);
                // clear store-persisted overrides for this strain so the
                // fresh layout isn't masked by old drag positions
                clearPositionOverrides(graph.strain.slug);
                setNodes((cur) => cur.map((fn) => {
                  const p = positions[fn.id];
                  return p ? { ...fn, position: p } : fn;
                }));
                requestAnimationFrame(() => { try { rf.fitView({ padding: DEFAULT_FIT_PADDING, maxZoom: 1.05 }); } catch {} });
              }}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:border-[var(--color-primary)] transition-colors shadow-card"
              title="Re-run auto-layout"
            >
              <Focus size={13} /> Relayout
            </button>
            <button
              onClick={() => setAddEdgeOpen(true)}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:border-[var(--color-primary)] transition-colors shadow-card"
            >
              <Plus size={13} /> Add edge
            </button>
            <button
              onClick={onToggleFullscreen}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:border-[var(--color-primary)] transition-colors shadow-card"
              title={fullscreen ? "Exit fullscreen" : "Fullscreen canvas"}
            >
              {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              <span className="hidden sm:inline">{fullscreen ? "Exit" : "Fullscreen"}</span>
            </button>
          </div>
        </Panel>

        {fullscreen && (
          <Panel position="top-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/90 backdrop-blur text-xs text-[var(--color-ink-muted)] shadow-card">
              <span className="font-mono">{graph.strain.title}</span>
              <span className="text-[var(--color-ink-faint)]">·</span>
              <button onClick={onToggleFullscreen} className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
                <Minimize2 size={12} /> exit
              </button>
            </div>
          </Panel>
        )}

        {/* filter panel */}
        {showFilters && (
          <Panel position="bottom-left">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-pop w-[260px]">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">Node types</div>
              <div className="flex flex-wrap gap-1 mb-3">
                {(Object.keys(NODE_TYPES) as NodeType[]).map((t) => {
                  const m = nodeMeta(t);
                  const active = nodeTypeFilter.has(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggleFilter(nodeTypeFilter, t, setNodeTypeFilter)}
                      className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border transition-colors",
                        active ? "border-[var(--color-primary)] text-[var(--color-ink)]" : "border-[var(--color-border)] text-[var(--color-ink-faint)] hover:text-[var(--color-ink-soft)]",
                      )}
                      style={{ color: active ? m.color : undefined }}
                    >
                      {m.short}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">Edge types</div>
              <div className="flex flex-wrap gap-1">
                {(Object.keys(EDGE_TYPES) as RelationType[]).map((t) => {
                  const m = edgeMeta(t);
                  const active = edgeTypeFilter.has(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggleFilter(edgeTypeFilter, t, setEdgeTypeFilter)}
                      className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border transition-colors",
                        active ? "border-[var(--color-primary)] text-[var(--color-ink)]" : "border-[var(--color-border)] text-[var(--color-ink-faint)] hover:text-[var(--color-ink-soft)]",
                      )}
                      style={{ color: active ? m.color : undefined }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {(nodeTypeFilter.size > 0 || edgeTypeFilter.size > 0) && (
                <button
                  onClick={() => { setNodeTypeFilter(new Set()); setEdgeTypeFilter(new Set()); }}
                  className="mt-3 text-[11px] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
                >
                  Clear filters
                </button>
              )}
            </div>
          </Panel>
        )}

        {/* add-node inline prompt */}
        {addNodeAt && (
          <Panel position="top-center">
            <div className="rounded-xl border border-[var(--color-primary)] bg-[var(--color-surface)] shadow-glow-primary p-2 flex items-center gap-2">
              <input
                autoFocus
                value={addNodeText}
                onChange={(e) => setAddNodeText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitAddNode(); if (e.key === "Escape") setAddNodeAt(null); }}
                placeholder="claim text…"
                className="bg-transparent text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] outline-none w-56"
              />
              <button onClick={commitAddNode} className="text-xs font-semibold text-[var(--color-primary)]">Add</button>
              <button onClick={() => setAddNodeAt(null)} className="text-xs text-[var(--color-ink-muted)]">Esc</button>
            </div>
          </Panel>
        )}
      </ReactFlow>

      <AddEdgeModal open={addEdgeOpen} onClose={() => setAddEdgeOpen(false)} graph={graph} />
    </div>
  );
}

function ToolButton({
  onClick, active, icon, label,
}: { onClick: () => void; active?: boolean; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs transition-colors",
        active
          ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
          : "text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]",
      )}
      title={label}
    >
      {icon}
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

export function GraphCanvas({
  graph, fullscreen, onToggleFullscreen,
}: {
  graph: StrainGraph;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}) {
  return (
    <ReactFlowProvider>
      <InnerCanvas graph={graph} fullscreen={fullscreen} onToggleFullscreen={onToggleFullscreen} />
    </ReactFlowProvider>
  );
}
